import { useQuery } from "@tanstack/react-query"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { extractErrorMessage } from "../../lib/api/errors"
import { queryKeys } from "../../lib/query-keys"
import type { IProps } from "./interfaces"
import { KnownVoiceCardView } from "./KnownVoiceCardView"
import type { IEnrollmentSample } from "./KnownVoiceCardView/interfaces"

/**
 * Smart wrapper for one known-voice row on the tenant voiceprint library page.
 * Owns the lazy voiceprint-sample query (fetched only once the row is expanded)
 * against the active-org speakers API; the revoke + delete mutations live on the
 * page so they can invalidate the shared list. KnownVoiceCardView is the pure
 * render surface.
 */
export function KnownVoiceCard({
  speaker,
  expanded,
  onToggle,
  onRevoke,
  onDelete,
  deleting,
  highlighted,
}: IProps): React.JSX.Element {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null

  const {
    data: samples,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.speakerEnrollments(orgId, speaker.id),
    queryFn: async (): Promise<IEnrollmentSample[]> => {
      if (!company) return []
      const res = await company.speakers({ speakerId: speaker.id }).enrollments.get()
      if (res.error) throw new Error(extractErrorMessage(res.error))
      // The Eden enrollment type is a structural superset of IEnrollmentSample
      // (it also carries confidence / model_version), so it assigns without a cast.
      return res.data?.data ?? []
    },
    enabled: expanded && !!company,
  })

  return (
    <KnownVoiceCardView
      name={speaker.name}
      title={speaker.title}
      email={speaker.email}
      recognizedMeetingCount={speaker.recognized_meeting_count}
      expanded={expanded}
      onToggle={onToggle}
      samples={samples ?? []}
      samplesLoading={isLoading && expanded}
      samplesError={error ? extractErrorMessage(error) : null}
      onRevoke={onRevoke}
      onDelete={onDelete}
      deleting={deleting}
      highlighted={highlighted}
    />
  )
}
